// src/app/services/posthog.service.ts
import { Injectable, NgZone } from '@angular/core';
import { FeatureFlag } from '@repo/models';
import posthog from 'posthog-js';
import { distinctUntilChanged, map, Observable, startWith, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class PosthogService {
  private featureFlagsChanged$ = new Subject<void>();
  private lastFeatureFlagsRefetchMs = 0;
  private static readonly FEATURE_FLAGS_REFETCH_THROTTLE_MS = 750;

  constructor(private ngZone: NgZone) {}

  public initPostHog() {
    this.ngZone.runOutsideAngular(async () => {
      posthog.init(environment.posthogKey, {
        api_host: environment.posthogProxyHost,
        ui_host: environment.posthogHost,
        defaults: '2026-01-30',
        loaded: async (posthog) => {
          try {
            const appVersion = environment.version || 'unknown';
            posthog.setPersonProperties({
              environment: environment.name,
              appVersion: `Portal - ${appVersion}`,
            });

            posthog.onFeatureFlags(() => {
              this.ngZone.run(() => {
                this.featureFlagsChanged$.next();
              });
            });

            try {
              posthog.reloadFeatureFlags();
            } catch (error) {
              console.error('Failed to reload feature flags:', error);
            }
            this.lastFeatureFlagsRefetchMs = Date.now();

            this.registerFeatureFlagsRefetchOnResume(posthog);

            posthog.capture('app_initialized');
          } catch (error) {
            console.error('Failed to initialize PostHog:', error);
          }
        },
      });
    });
  }

  public capture(
    event: string,
    properties?: Record<string, string | number | boolean | null>
  ): void {
    posthog.capture(event, properties);
  }

  public isFeatureEnabled(featureKey: FeatureFlag): boolean {
    return posthog.isFeatureEnabled(featureKey) ?? false;
  }

  public isFeatureEnabledWithDefault(featureKey: FeatureFlag, defaultValue: boolean): boolean {
    const value = posthog.getFeatureFlag(featureKey);
    if (value === undefined || value === null) {
      return defaultValue;
    }
    return value === true;
  }

  public observeFeatureEnabled(featureKey: FeatureFlag): Observable<boolean> {
    return this.featureFlagsChanged$.pipe(
      startWith(undefined),
      map(() => this.isFeatureEnabled(featureKey)),
      distinctUntilChanged()
    );
  }

  public observeFeatureEnabledWithDefault(
    featureKey: FeatureFlag,
    defaultValue: boolean
  ): Observable<boolean> {
    return this.featureFlagsChanged$.pipe(
      startWith(undefined),
      map(() => this.isFeatureEnabledWithDefault(featureKey, defaultValue)),
      distinctUntilChanged()
    );
  }

  public observeAvailableLanguages(): Observable<string[] | undefined> {
    return this.featureFlagsChanged$.pipe(
      startWith(undefined),
      map(() => this.getAvailableLanguages()),
      distinctUntilChanged((previous, current) => this.areArraysEqual(previous, current))
    );
  }

  public getAvailableLanguages(): string[] | undefined {
    const result = posthog.getFeatureFlagResult(FeatureFlag.AvailableLanguages);
    if (
      !environment?.name ||
      !result?.payload ||
      typeof result.payload !== 'object' ||
      Array.isArray(result.payload)
    ) {
      return undefined;
    }

    return (
      ((result.payload as Record<string, unknown>)[environment.name] as string[] | undefined) ??
      undefined
    );
  }

  private areArraysEqual(previous?: string[], current?: string[]): boolean {
    if (previous === current) {
      return true;
    }

    if (!previous || !current || previous.length !== current.length) {
      return false;
    }

    return previous.every((value, index) => value === current[index]);
  }

  /**
   * Re-fetch feature flags when the page is shown again (Page Visibility API: `visibilitychange` →
   * `visible`). That covers: switching back to this tab, restoring a minimized window, and bringing
   * the browser to the front after another app was on top — i.e. when the document was in the
   * background and is presented to the user again. Throttled to avoid many requests if visibility
   * flutters. `onFeatureFlags` re-emits and `featureFlagsChanged$` propagates to consumers.
   */
  private registerFeatureFlagsRefetchOnResume(client: { reloadFeatureFlags: () => void }): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.throttledReloadFeatureFlags(client);
      }
    });
  }

  private throttledReloadFeatureFlags(client: { reloadFeatureFlags: () => void }): void {
    const now = Date.now();
    if (now - this.lastFeatureFlagsRefetchMs < PosthogService.FEATURE_FLAGS_REFETCH_THROTTLE_MS) {
      return;
    }
    this.lastFeatureFlagsRefetchMs = now;
    this.ngZone.runOutsideAngular(() => {
      try {
        client.reloadFeatureFlags();
      } catch (error) {
        console.error('Failed to reload feature flags on resume', error);
      }
    });
  }
}
